import { Card, CardContent } from '@/components/ui/card';
import { ChevronRight } from 'lucide-react';

import type { AlvoStatus, StatusAlvo } from '../../../api/monitoramento';
import {
  EXPERIENCE_GROUPS,
  alvosComProblema,
  alvosDoGrupo,
  statusAgregado,
  type ExperienceGroupId,
} from '../../../lib/monitoramento/experienceGrouping';
import {
  useResumoJobs,
  useSerieAgregada,
} from '../../../hooks/useMonitoramentoQuery';
import { SparklineMini } from './SparklineMini';

/**
 * Header de saúde por experiência — 5 cards de alvos externos + 1 card de
 * pipeline interna. Responde "tem algo fora AGORA?" em uma olhada de 6 pills.
 *
 * <p>A cor do pill (status) usa a janela curta (last-known do snapshot atual).
 * Os números agregados (% sucesso, contagem) usam a mesma janela do snapshot —
 * a UI exibe a janela escolhida pelo backend (atualmente 1h via SLI horário).
 */
export interface SaudeGruposCardsProps {
  alvos: AlvoStatus[];
  grupoSelecionado: ExperienceGroupId | null;
  onSelecionarGrupo: (grupo: ExperienceGroupId | null) => void;
}

export function SaudeGruposCards({ alvos, grupoSelecionado, onSelecionarGrupo }: SaudeGruposCardsProps) {
  const resumoJobs = useResumoJobs(30_000);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
      {EXPERIENCE_GROUPS.map((grupo) => {
        if (grupo.id === 'pipeline_interna') {
          const dados = resumoJobs.data;
          const status = derivarStatusJobs(dados);
          // "OPERANDO": cor cinza (não dá pra afirmar saúde por taxa) MAS há
          // sinal de vida — pendentes, em execução ou jobs finalizados nas
          // últimas 24h. Distingue "monitor sem amostra suficiente" de
          // "monitor sem nenhum dado" (ex.: ambiente recém-deployado, fim
          // de semana ocioso, dev local).
          const totalFinalizados1h = (dados?.sucesso1h ?? 0) + (dados?.falha1h ?? 0);
          const totalFinalizados24h = (dados?.sucesso24h ?? 0) + (dados?.falha24h ?? 0);
          const atividade = (dados?.pendentes ?? 0) + (dados?.emExecucao ?? 0) + totalFinalizados24h;
          const operando = status === 'SEM_DADOS' && atividade > 0;

          let valorPrincipal: string;
          if (operando) {
            valorPrincipal = totalFinalizados1h >= 1
              ? `${totalFinalizados1h} ${totalFinalizados1h === 1 ? 'job' : 'jobs'} em 1h`
              : totalFinalizados24h >= 1
                ? `${totalFinalizados24h} ${totalFinalizados24h === 1 ? 'job' : 'jobs'} em 24h`
                : 'aguardando finalização';
          } else if (dados?.percentualSucesso1h !== null && dados?.percentualSucesso1h !== undefined) {
            valorPrincipal = `${formatPercent(dados.percentualSucesso1h)} sucesso`;
          } else {
            valorPrincipal = 'sem dados';
          }
          const linhaSecundaria = dados
            ? `${dados.pendentes} pend · ${dados.emExecucao} em exec.`
            : '—';
          return (
            <CardGrupo
              key={grupo.id}
              titulo={grupo.label}
              descricao={grupo.descricaoCurta}
              status={status}
              rotuloStatusOverride={operando ? 'OPERANDO' : undefined}
              detalhe={valorPrincipal}
              detalheSecundario={linhaSecundaria}
              selecionado={grupoSelecionado === grupo.id}
              onClick={() =>
                onSelecionarGrupo(grupoSelecionado === grupo.id ? null : grupo.id)
              }
              alvosParaSparkline={[]}
            />
          );
        }

        const alvosDoCard = alvosDoGrupo(grupo.id, alvos);
        const status = statusAgregado(alvosDoCard);
        const problemas = alvosComProblema(alvosDoCard);
        const detalhe = alvosDoCard.length === 0
          ? 'sem chamadas registradas'
          : problemas > 0
            ? `${problemas}/${alvosDoCard.length} alvo${alvosDoCard.length > 1 ? 's' : ''} com problema`
            : `${alvosDoCard.length} alvo${alvosDoCard.length > 1 ? 's' : ''} ativo${alvosDoCard.length > 1 ? 's' : ''}`;
        const detalheSecundario = alvosDoCard.length === 0
          ? '—'
          : descreverPiorAlvo(alvosDoCard);
        return (
          <CardGrupo
            key={grupo.id}
            titulo={grupo.label}
            descricao={grupo.descricaoCurta}
            status={status}
            detalhe={detalhe}
            detalheSecundario={detalheSecundario}
            selecionado={grupoSelecionado === grupo.id}
            onClick={() =>
              onSelecionarGrupo(grupoSelecionado === grupo.id ? null : grupo.id)
            }
            alvosParaSparkline={alvosDoCard.map((a) => a.alvo)}
          />
        );
      })}
    </div>
  );
}

function CardGrupo({
  titulo,
  descricao,
  status,
  rotuloStatusOverride,
  detalhe,
  detalheSecundario,
  selecionado,
  onClick,
  alvosParaSparkline,
}: {
  titulo: string;
  descricao: string;
  status: StatusAlvo;
  /** Sobrescreve o texto do pill mantendo a cor derivada do status. */
  rotuloStatusOverride?: string;
  detalhe: string;
  detalheSecundario: string;
  selecionado: boolean;
  onClick: () => void;
  alvosParaSparkline: string[];
}) {
  // Hook chamado dentro de cada CardGrupo — ordem é estável porque
  // EXPERIENCE_GROUPS é constante. React Query cacheia por chave (alvos
  // + janela), então re-renders não disparam fetch.
  const serieQuery = useSerieAgregada(alvosParaSparkline, '24h', alvosParaSparkline.length > 0);

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className={`cursor-pointer transition-colors hover:bg-muted/40 ${
        selecionado ? 'ring-2 ring-primary' : ''
      }`}
    >
      <CardContent className="flex min-w-0 flex-col gap-1.5 p-4">
        <div className="flex min-w-0 items-center justify-between gap-2">
          <PillStatus status={status} rotuloOverride={rotuloStatusOverride} />
          <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
        </div>
        <span className="break-words text-sm font-semibold leading-tight">{titulo}</span>
        <span className="break-words text-xs text-muted-foreground" title={descricao}>
          {detalhe}
        </span>
        <span className="break-words text-xs text-muted-foreground/80">{detalheSecundario}</span>
        {alvosParaSparkline.length > 0 && (
          <div className="mt-1">
            <SparklineMini buckets={serieQuery.data?.buckets ?? []} totalBarras={24} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PillStatus({ status, rotuloOverride }: { status: StatusAlvo; rotuloOverride?: string }) {
  const classes = classesPill(status);
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${classes}`}>
      <span className={`size-2 rounded-full ${classesBolinha(status)}`} aria-hidden="true" />
      {rotuloOverride ?? rotuloStatus(status)}
    </span>
  );
}

function rotuloStatus(status: StatusAlvo): string {
  switch (status) {
    case 'ATIVO':
      return 'ATIVO';
    case 'DEGRADADO':
      return 'DEGRADADO';
    case 'INDISPONIVEL':
      return 'INDISPONÍVEL';
    case 'SEM_DADOS':
      return 'SEM DADOS';
  }
}

function classesPill(status: StatusAlvo): string {
  switch (status) {
    case 'ATIVO':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'DEGRADADO':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'INDISPONIVEL':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    case 'SEM_DADOS':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
  }
}

function classesBolinha(status: StatusAlvo): string {
  switch (status) {
    case 'ATIVO':
      return 'bg-green-500';
    case 'DEGRADADO':
      return 'bg-yellow-500';
    case 'INDISPONIVEL':
      return 'bg-red-500';
    case 'SEM_DADOS':
      return 'bg-gray-400';
  }
}

function descreverPiorAlvo(alvos: AlvoStatus[]): string {
  // Encontra o alvo mais "doente" para destacar no card. Útil para responder
  // "qual tribunal está fora?" sem precisar abrir o drill-in.
  const peso: Record<StatusAlvo, number> = {
    INDISPONIVEL: 4,
    DEGRADADO: 3,
    SEM_DADOS: 1,
    ATIVO: 0,
  };
  const ordenado = [...alvos].sort((a, b) => peso[b.status] - peso[a.status]);
  const primeiro = ordenado[0];
  if (!primeiro) return '—';
  if (primeiro.status === 'ATIVO') {
    const p95 = primeiro.latencia_p95_1h_ms;
    return p95 != null ? `p95 ${formatLatencia(p95)}` : 'todos OK';
  }
  return `${primeiro.alvo} (${rotuloStatus(primeiro.status).toLowerCase()})`;
}

/**
 * Mínimo de jobs finalizados em 1h para a porcentagem de sucesso ser
 * considerada estatisticamente significativa. Mesma filosofia do
 * `min-amostras-para-taxa` do detector de alvos: evita "1 falha em 1 amostra
 * = 0% de sucesso = INDISPONIVEL" e o simétrico "1 sucesso = 100% = ATIVO".
 */
const MIN_FINALIZADOS_PARA_TAXA = 3;

function derivarStatusJobs(
  dados: { percentualSucesso1h: number | null; sucesso1h: number; falha1h: number } | undefined,
): StatusAlvo {
  if (!dados) return 'SEM_DADOS';
  if (dados.percentualSucesso1h === null) return 'SEM_DADOS';
  // Anti-volatilidade: pouca evidência → não pintamos a cor da pill com
  // base em uma porcentagem instável. Atividade (pendentes / em execução)
  // continua visível na linha secundária do card.
  const totalFinalizados = dados.sucesso1h + dados.falha1h;
  if (totalFinalizados < MIN_FINALIZADOS_PARA_TAXA) return 'SEM_DADOS';
  if (dados.percentualSucesso1h < 50) return 'INDISPONIVEL';
  if (dados.percentualSucesso1h < 90) return 'DEGRADADO';
  return 'ATIVO';
}

function formatPercent(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return '—';
  if (valor >= 99.95) return '100%';
  if (valor < 10) return `${valor.toFixed(1)}%`;
  return `${Math.round(valor)}%`;
}

function formatLatencia(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}
